/**
 * SwimlaneContainer.tsx
 *
 * Multi-Axis Swimlane Container for TaskFlow Board and Matrix views.
 * Supports grouping by Type (Goal, Plan, Project, Task, Action), Priority, or Primary Tag.
 */

import React, { useState } from 'react';
import type { TaskFlowItem } from '../../../plugins/taskflow/contracts/taskflow-schema.js';

export type SwimlaneGroupingAxis = 'none' | 'type' | 'priority' | 'tag';

export interface SwimlaneGroup {
  id: string;
  title: string;
  items: TaskFlowItem[];
}

export interface SwimlaneContainerProps {
  items: TaskFlowItem[];
  groupingAxis: SwimlaneGroupingAxis;
  renderGroupContent: (groupItems: TaskFlowItem[], groupId: string) => React.ReactNode;
}

export const SwimlaneContainer: React.FC<SwimlaneContainerProps> = ({
  items,
  groupingAxis,
  renderGroupContent,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (groupingAxis === 'none') {
    return <>{renderGroupContent(items, 'all')}</>;
  }

  // Partition items into groups
  const groupsMap = new Map<string, TaskFlowItem[]>();

  if (groupingAxis === 'type') {
    const types = ['goal', 'plan', 'project', 'task', 'action'];
    for (const t of types) groupsMap.set(t, []);
    for (const item of items) {
      const list = groupsMap.get(item.type) || [];
      list.push(item);
      groupsMap.set(item.type, list);
    }
  } else if (groupingAxis === 'priority') {
    const priorities = ['urgent', 'high', 'medium', 'normal', 'low'];
    for (const p of priorities) groupsMap.set(p, []);
    for (const item of items) {
      const p = item.priority || 'normal';
      const list = groupsMap.get(p) || [];
      list.push(item);
      groupsMap.set(p, list);
    }
  } else if (groupingAxis === 'tag') {
    for (const item of items) {
      const tag = item.tags && item.tags.length > 0 ? item.tags[0] : 'untagged';
      const list = groupsMap.get(tag) || [];
      list.push(item);
      groupsMap.set(tag, list);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {Array.from(groupsMap.entries()).map(([groupId, groupItems]) => {
        if (groupItems.length === 0 && groupingAxis === 'tag') return null;
        const isCollapsed = collapsedGroups.has(groupId);

        return (
          <div
            key={groupId}
            style={{
              backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.02))',
              border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            {/* Swimlane Header */}
            <div
              onClick={() => toggleGroup(groupId)}
              style={{
                padding: '10px 16px',
                backgroundColor: 'var(--theme-bg-header, rgba(15, 23, 42, 0.5))',
                borderBottom: isCollapsed ? 'none' : '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                  {isCollapsed ? '▶' : '▼'}
                </span>
                <span style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize', color: 'var(--theme-text-primary, #f8fafc)' }}>
                  {groupId}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
                    color: 'var(--theme-text-muted, #94a3b8)',
                    padding: '1px 6px',
                    borderRadius: '10px',
                  }}
                >
                  {groupItems.length}
                </span>
              </div>
            </div>

            {/* Swimlane Content */}
            {!isCollapsed && (
              <div style={{ padding: '8px' }}>
                {renderGroupContent(groupItems, groupId)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
